<template>
  <div class="left-chart-top-wrap">
    <!-- left chart title  -->
    <div class="chart-title d-flex flex-wrap justify-content-between">
      <div class="d-flex" style="gap: 3px;">
        <h6 class="mb-0">
          {{ $t('dashboard.chart.sector') }}: {{ $store.state.ticker.sector }}
          <span class="title-question-icon" @click.prevent="toggleHelp()">
            <iquestion />
          </span>
        </h6>
        <header-radio
          v-if="chartTypes.length > 1"
          :selected="selectedChart"
          :options="chartTypes"
          @click="changeChart"
          :comparator="(ch, selected) => ch === selected.id"
        >
          <template v-slot:default="{ option: chart }">
            {{ $t(`dashboard.chart.graph.${chart}`) }}
          </template>
        </header-radio>
      </div>
      <div class="about-company" @click="openInfo" style="line-height: 19px;">
        {{ $t('dashboard.chart.companyInfo') }}
      </div>
    </div>
    <div class="chart-img settings-tab">
      <market-chart v-if="selectedChart.id === 'usual'" ref="marketChartDiv" />
      <chart-widget-tw v-if="selectedChart.id === 'tv'"></chart-widget-tw>
      <chart-timing-selector
        v-if="selectedChart.id === 'usual'"
        :items="possibleIntervals"
        :selected="selectedInterval"
        @click="changeInterval"
      />

      <!-- left chart-mid  -->
      <div class="left-chart-mid">
        <div class="chart-items">
          <div
            class="single-chart-number"
            v-for="(metric, i) in metrics"
            :key="i"
          >
            <p>{{ metric.title }}</p>
            <h6>{{ metric.value }}</h6>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get as _get } from 'lodash'
import { mapState } from 'vuex'

import { getCap, financial } from '@/utils/filters'

import iquestion from '@/components/icons/question'
import MarketChart from './MarketChart/index.vue'
import headerRadioButton from '@/components/library/headerRadioButton'
import ChartTimingSelector from './ChartTimingSelect.vue'
import ChartWidgetTW from './ChartWidgetTW.vue'

const TRADINGVIEW_NOT_AVAILABLE = ['EPA', 'MOEX']

export default {
  methods: {
    toggleHelp() {
      this.$store.commit('layout/updateModal', {
        modal: 'help',
        show: true,
        title: this.$t('modals.references.sector.title'),
        content: this.$t('modals.references.sector.content'),
        subtitle: this.$t('modals.messages.reference'),
      })
    },
    openInfo() {
      this.$store.commit('layout/updateModal', {
        modal: 'help',
        show: true,
        title: `${this.companyName} (${this.ticker})`,
        subtitle: this.$t('modals.messages.companyInfo'),
        content: this.tickerDescription,
      })
    },
    changeInterval(v) {
      this.selectedInterval = v
      this.$refs.marketChartDiv.updateInterval(v)
    },
    setDefaultChart() {
      this.selectedChart = { id: this.chartTypes[0] }
    },
    changeChart(v) {
      this.selectedChart = { id: v }
    },
    getCap: getCap,
  },
  data() {
    return {
      possibleIntervals: ['1m', '15m', '1h', '1d'].map((t) => ({
        value: t,
        key: t,
      })),
      selectedChart: { id: 'tv' },
      selectedInterval: '15m',
    }
  },
  mounted() {
    this.setDefaultChart()
  },
  watch: {
    '$store.state.market.platform'() {
      this.setDefaultChart()
    },
  },
  computed: {
    ...mapState('layout', {
      themeBlack: (state) => state.themeBlack,
    }),
    ...mapState('market', {
      ticker: (state) => state.ticker,
      tickerDescription: (state) => state.companyDescription,
      companyName: (state) => state.companyName,
      metricsNum: (state) => {
        return {
          cap: state.currentPrice * state.marketCapNum * state.adrCoeff,
          day: {
            l: _get(state, 'metricsDay.l', 0),
            h: _get(state, 'metricsDay.h', 0),
          },
          year: {
            l: _get(state, 'metrics52w.l', 0),
            h: _get(state, 'metrics52w.h', 0),
          },
          volume: _get(state, 'metrics3m.v', '--'),
        }
      },
      chartTypes: (state) => {
        // trading view is not available for MOEX
        if (TRADINGVIEW_NOT_AVAILABLE.indexOf(state.platform) > -1)
          return ['usual']
        return ['tv', 'usual']
      },
    }),
    metrics() {
      return [
        {
          title: this.$t('dashboard.chart.cap'),
          value: this.getCap(this.metricsNum.cap),
        },
        {
          title: this.$t('dashboard.chart.dayRange'),
          value: `${financial(this.metricsNum.day.l)} - ${financial(
            this.metricsNum.day.h
          )}`,
        },
        {
          title: this.$t('dashboard.chart.yearRange'),
          value: `${financial(this.metricsNum.year.l)} - ${financial(
            this.metricsNum.year.h
          )}`,
        },
        {
          title: this.$t('dashboard.chart.quarterVolume'),
          value: this.getCap(this.metricsNum.volume),
        },
      ]
    },
  },
  components: {
    iquestion: iquestion,
    'market-chart': MarketChart,
    'chart-timing-selector': ChartTimingSelector,
    'chart-widget-tw': ChartWidgetTW,
    'header-radio': headerRadioButton,
  },
}
</script>

<style lang="scss">
.Custom_Scrollbar {
  @include scrollbars(8px, rgba(#c2c2d6, 0.4), rgba(#c2c2d6, 0.2));
  @include transition();
  &:hover {
    @include scrollbars(8px, rgba(#c2c2d6, 1), rgba(#c2c2d6, 0.2));
  }
}

.chart-title {
  line-height: 1;
  .about-company {
    color: #00a6cb;
    font-weight: bold;
    &:hover {
      cursor: pointer;
    }
  }

  .hover-action {
    span.text {
      display: inline-block;
    }
    .view-item {
      display: none;
      color: $primary-color2;

      a {
        color: $primary-color2;
      }
      svg {
        cursor: pointer;
      }
    }
    &:hover {
      span.text {
        display: none;
      }
      .view-item {
        display: inline-block;
      }
    }
  }
}

.chart-img {
  .market-chart-copyright {
    font-size: 0.625rem;
    font-style: italic;
    opacity: 0.7;
    position: absolute;
    z-index: 799;
    bottom: 1.5rem;
    left: 0;
  }
}
.left-chart-mid {
  .chart-items {
    display: flex;
    margin: 10px 0 5px;
    overflow: auto;

    .single-chart-number {
      position: relative;
      padding-right: 20px;
      padding-left: 10px;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;

        &:after {
          display: none;
        }
      }

      &:after {
        position: absolute;
        right: 0;
        top: 0;
        width: 1px;
        height: 90%;
        background: #c2c2d6;
        content: '';
      }

      p {
        font-size: 0.75rem;
        font-weight: 700;
        margin-bottom: 5px;
        font-weight: normal;
      }

      h6 {
        font-size: 1rem;
        font-weight: 400;
      }
    }
  }

  h6 {
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
  }
}

.left-chart-bottom {
  .left-bottom-list-wrap {
    overflow-y: auto;
    height: 160px;
    // max-height: calc(100vh - 790px);
    // min-height: 90px;

    // @media (max-width: 1400px), (max-height: 800px) {
    //   max-height: calc(100vh - 524px) !important;
    //   min-height: auto;
    // }

    // @media (max-width: 1400px) {
    //   max-height: calc(100vh - 690px);
    //   min-height: 97px;
    // }

    @media (max-width: 1240px) {
      max-height: 167px;
    }
    @extend .Custom_Scrollbar;

    .single-footer-chart-list {
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      @media (max-width: 1400px) {
        margin-bottom: 5px;
      }
      @media (max-width: 1240px) {
        margin-bottom: 10px;
      }

      .time {
        margin-bottom: 0;
        font-size: 1.125rem;
      }

      h6 {
        margin-bottom: 0;
        margin-left: 15px;
        font-weight: 400;
        @include transition();
        cursor: pointer;

        &:hover {
          color: $primary-color2;
        }
      }
    }
  }
}
// ,
// .right-top-chart,
// .right-mid-chart-left
.left-chart-top-wrap {
  margin-bottom: 30px;
}
</style>
